import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Users, FileText, Building2, TrendingUp, Loader2,
  Settings, Star, CreditCard, Search, ShieldAlert, BarChart3, Euro, X as XIcon, BellRing, Landmark
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { LeadsTab } from "@/components/admin/LeadsTab";
import { ContractorsTab } from "@/components/admin/ContractorsTab";
import { NotificationsLogTab } from "@/components/admin/NotificationsLogTab";
import { TransfersTab } from "@/components/admin/TransfersTab";
import { ReviewsTab, PaymentsTab } from "@/components/admin/ReviewsPaymentsTabs";
import { AnalyticsTab, PricingTab, SettingsTab, SecurityTab } from "@/components/admin/OtherTabs";
import { BlogTab } from "@/components/admin/BlogTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [trashLeads, setTrashLeads] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [showContractorTrash, setShowContractorTrash] = useState(false);
  const [contractorTrash, setContractorTrash] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ project_type: "", city: "", status: "" });

  const [authenticated, setAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState("");

  const [adminSettings, setAdminSettings] = useState({ admin_email: "", new_password: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState("pending");
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [moderatingId, setModeratingId] = useState(null);

  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [revenueGoal, setRevenueGoal] = useState(0);
  const [goalInput, setGoalInput] = useState("");

  const [securityEvents, setSecurityEvents] = useState([]);
  const [adminLogins, setAdminLogins] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [pdfMonth, setPdfMonth] = useState(new Date().toISOString().slice(0, 7));

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  const [leadPricing, setLeadPricing] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [editingPrices, setEditingPrices] = useState({});
  const [isEditingPricing, setIsEditingPricing] = useState(false);

  useEffect(() => { checkAuth(); }, []);

  useEffect(() => {
    if (authenticated) {
      fetchData();
      fetchSettings();
      fetchReviews();
      fetchPayments();
      fetchAnalytics();
      fetchPricing();
      fetchSecurityEvents();
    }
  }, [currentPage, filters, authenticated, reviewFilter]);

  useEffect(() => {
    if (!authenticated) return;
    if (searchQuery.trim().length < 2) { setSearchResults(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/admin/search?q=${encodeURIComponent(searchQuery.trim())}`, { withCredentials: true });
        setSearchResults(res.data);
      } catch (error) { setSearchResults(null); }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, authenticated]);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (res.data && res.data.role === "admin") {
        setAuthenticated(true);
        setAdminEmail(res.data.email);
      } else { navigate("/admin/login"); }
    } catch (error) { navigate("/admin/login"); }
    finally { setAuthLoading(false); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      if (filters.project_type) params.append("project_type", filters.project_type);
      if (filters.city) params.append("city", filters.city);
      if (filters.status) params.append("status", filters.status);

      const [leadsRes, contractorsRes, statsRes, trashRes, contractorTrashRes] = await Promise.all([
        axios.get(`${API}/leads?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/contractors`, { withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/leads/trash`, { withCredentials: true }),
        axios.get(`${API}/contractors/trash`, { withCredentials: true })
      ]);

      setLeads(leadsRes.data.leads);
      setSelectedLeads([]);
      setSelectedContractors([]);
      setTrashLeads(trashRes.data);
      setContractorTrash(contractorTrashRes.data);
      setTotalPages(leadsRes.data.pages);
      setContractors(contractorsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) { navigate("/admin/login"); }
      else { toast.error("Erreur lors du chargement des données"); }
    } finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setAdminSettings({ admin_email: res.data.admin_email || "", new_password: "" });
    } catch (error) { console.error(error); }
  };

  const fetchReviews = async () => {
    try {
      const params = reviewFilter ? `?status=${reviewFilter}` : "";
      const res = await axios.get(`${API}/reviews/admin${params}`, { withCredentials: true });
      setReviews(res.data);
      setSelectedReviews([]);
    } catch (error) { console.error(error); }
  };

  const fetchPayments = async () => {
    try {
      const res = await axios.get(`${API}/admin/payments`, { withCredentials: true });
      setPayments(res.data);
      setSelectedPayments([]);
    } catch (error) { console.error(error); }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [res, goalRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/revenue-goal`, { withCredentials: true })
      ]);
      setAnalytics(res.data);
      setRevenueGoal(goalRes.data.amount || 0);
      setGoalInput(goalRes.data.amount ? String(goalRes.data.amount) : "");
    } catch (error) { console.error(error); }
    finally { setAnalyticsLoading(false); }
  };

  const handleSaveRevenueGoal = async () => {
    const amount = parseFloat(goalInput);
    if (isNaN(amount) || amount < 0) { toast.error("Montant invalide"); return; }
    try {
      await axios.put(`${API}/admin/revenue-goal`, { amount }, { withCredentials: true });
      setRevenueGoal(amount);
      toast.success("Objectif mensuel enregistré");
    } catch (error) { toast.error("Erreur lors de l'enregistrement de l'objectif"); }
  };

  const handleExportContractors = async (format) => {
    try {
      const response = await axios.get(`${API}/contractors/export/${format}`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", format === "csv" ? "entrepreneurs.csv" : "entrepreneurs.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export téléchargé");
    } catch (error) { toast.error("Erreur lors de l'export"); }
  };

  const fetchPricing = async () => {
    setPricingLoading(true);
    try {
      const res = await axios.get(`${API}/admin/lead-pricing`, { withCredentials: true });
      setLeadPricing(res.data);
      const priceMap = {};
      res.data.forEach(x => { priceMap[x.category] = x.price; });
      setEditingPrices(priceMap);
    } catch (error) { console.error(error); }
    finally { setPricingLoading(false); }
  };

  const fetchSecurityEvents = async () => {
    try {
      const [eventsRes, statsRes, loginsRes] = await Promise.all([
        axios.get(`${API}/admin/security-events`, { withCredentials: true }),
        axios.get(`${API}/admin/email-stats`, { withCredentials: true }),
        axios.get(`${API}/admin/login-log`, { withCredentials: true })
      ]);
      setSecurityEvents(eventsRes.data);
      setEmailStats(statsRes.data);
      setAdminLogins(loginsRes.data);
    } catch (error) { console.error(error); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) {}
    navigate("/admin/login");
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const updateData = {};
      if (adminSettings.admin_email) updateData.admin_email = adminSettings.admin_email;
      await axios.put(`${API}/admin/settings`, updateData, { withCredentials: true });
      toast.success("Paramètres enregistrés");
      setAdminSettings(prev => ({ ...prev, new_password: "" }));
    } catch (error) { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSavingSettings(false); }
  };

  const handleModerate = async (reviewId, status) => {
    setModeratingId(reviewId);
    try {
      await axios.put(`${API}/reviews/${reviewId}/moderate`, { status }, { withCredentials: true });
      toast.success(status === "approved" ? "Avis approuvé" : "Avis rejeté");
      fetchReviews();
    } catch (error) { toast.error("Erreur lors de la modération"); }
    finally { setModeratingId(null); }
  };

  const toggleReviewSelect = (id) => {
    setSelectedReviews(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAllReviews = () => {
    setSelectedReviews(prev => prev.length === reviews.length ? [] : reviews.map(r => r.id));
  };

  const handleBulkDeleteReviews = async () => {
    if (!window.confirm(`Supprimer définitivement ${selectedReviews.length} avis ? Cette action est irréversible.`)) return;
    try {
      const res = await axios.post(`${API}/reviews/admin/bulk-delete`, { review_ids: selectedReviews }, { withCredentials: true });
      toast.success(`${res.data.deleted} avis supprimé(s)`);
      setSelectedReviews([]);
      fetchReviews();
    } catch (error) { toast.error("Erreur lors de la suppression"); }
  };

  const handleSeedPricing = async () => {
    try {
      const res = await axios.post(`${API}/admin/lead-pricing/seed`, {}, { withCredentials: true });
      toast.success(`${res.data.seeded} prix de leads initialisés`);
      fetchPricing();
    } catch (error) { toast.error("Erreur lors de l'initialisation"); }
  };

  const handleSaveAllPricing = async () => {
    try {
      const prices = Object.entries(editingPrices).map(([category, price]) => ({ category, price: parseFloat(price) || 0 }));
      await axios.put(`${API}/admin/lead-pricing/bulk`, { prices }, { withCredentials: true });
      toast.success("Prix des leads mis à jour");
      setIsEditingPricing(false);
      fetchPricing();
    } catch (error) { toast.error("Erreur lors de la sauvegarde"); }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/leads/export/${format}`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leads.${format === "excel" ? "xlsx" : "csv"}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (error) { toast.error("Erreur lors de l'export"); }
  };

  const handleExportPaymentsPdf = async () => {
    try {
      const response = await axios.get(`${API}/admin/payments/export-pdf?month=${pdfMonth}`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `paiements_${pdfMonth}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export PDF téléchargé");
    } catch (error) { toast.error("Erreur lors de l'export PDF"); }
  };

  const handleSendMonthlyReport = async () => {
    try {
      const res = await axios.post(`${API}/admin/monthly-report/send?month=${pdfMonth}`, {}, { withCredentials: true });
      toast.success(`Rapport ${res.data.month} envoyé par email`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du rapport");
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}/status?status=${newStatus}`, {}, { withCredentials: true });
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error) { toast.error("Erreur lors de la mise à jour"); }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Mettre ce lead à la corbeille ? (récupérable pendant 30 jours)")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, { withCredentials: true });
      toast.success("Lead mis à la corbeille");
      fetchData();
    } catch (error) { toast.error("Erreur lors de la suppression"); }
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => prev.includes(leadId) ? prev.filter(id => id !== leadId) : [...prev, leadId]);
  };
  const toggleSelectAll = () => {
    setSelectedLeads(prev => prev.length === leads.length ? [] : leads.map(l => l.id));
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Mettre ${selectedLeads.length} lead(s) à la corbeille ? (récupérable pendant 30 jours)`)) return;
    try {
      const res = await axios.post(`${API}/leads/bulk-delete`, { lead_ids: selectedLeads }, { withCredentials: true });
      toast.success(`${res.data.deleted} lead(s) mis à la corbeille`);
      fetchData();
    } catch (error) { toast.error("Erreur lors de la suppression groupée"); }
  };

  const handlePublish = async (leadId) => {
    try {
      await axios.post(`${API}/leads/${leadId}/publish`, {}, { withCredentials: true });
      toast.success("Lead envoyé aux entreprises");
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur lors de l'envoi"); }
  };

  const handleBulkPublish = async () => {
    try {
      const res = await axios.post(`${API}/leads/bulk-publish`, { lead_ids: selectedLeads }, { withCredentials: true });
      toast.success(`${res.data.published} lead(s) envoyé(s) aux entreprises`);
      setSelectedLeads([]);
      fetchData();
    } catch (error) { toast.error("Erreur lors de l'envoi groupé"); }
  };

  const handleBulkStatus = async (status) => {
    try {
      const res = await axios.post(`${API}/leads/bulk-status`, { lead_ids: selectedLeads, status }, { withCredentials: true });
      toast.success(`${res.data.updated} lead(s) mis à jour`);
      fetchData();
    } catch (error) { toast.error("Erreur lors de la mise à jour groupée"); }
  };

  const toggleTrashView = () => { setShowTrash(prev => !prev); setSelectedLeads([]); };
  const toggleTrashSelectAll = () => {
    setSelectedLeads(prev => prev.length === trashLeads.length ? [] : trashLeads.map(l => l.id));
  };

  const handleBulkRestore = async () => {
    try {
      const res = await axios.post(`${API}/leads/restore`, { lead_ids: selectedLeads }, { withCredentials: true });
      toast.success(`${res.data.restored} lead(s) restauré(s)`);
      fetchData();
    } catch (error) { toast.error("Erreur lors de la restauration"); }
  };

  const handleBulkPurge = async () => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ${selectedLeads.length} lead(s) ? Cette action est irréversible.`)) return;
    try {
      const res = await axios.post(`${API}/leads/purge`, { lead_ids: selectedLeads }, { withCredentials: true });
      toast.success(`${res.data.purged} lead(s) supprimé(s) définitivement`);
      fetchData();
    } catch (error) { toast.error("Erreur lors de la suppression définitive"); }
  };

  const handleRestoreLead = async (leadId) => {
    try {
      await axios.post(`${API}/leads/restore`, { lead_ids: [leadId] }, { withCredentials: true });
      toast.success("Lead restauré");
      fetchData();
    } catch (error) { toast.error("Erreur lors de la restauration"); }
  };

  const handlePurgeLead = async (leadId) => {
    if (!window.confirm("Supprimer DÉFINITIVEMENT ce lead ? Cette action est irréversible.")) return;
    try {
      await axios.post(`${API}/leads/purge`, { lead_ids: [leadId] }, { withCredentials: true });
      toast.success("Lead supprimé définitivement");
      fetchData();
    } catch (error) { toast.error("Erreur lors de la suppression définitive"); }
  };

  const toggleContractorSelection = (contractorId) => {
    setSelectedContractors(prev => prev.includes(contractorId) ? prev.filter(id => id !== contractorId) : [...prev, contractorId]);
  };
  const toggleContractorSelectAll = () => {
    const list = showContractorTrash ? contractorTrash : contractors;
    setSelectedContractors(prev => prev.length === list.length ? [] : list.map(c => c.id));
  };

  const toggleContractorTrashView = () => {
    setShowContractorTrash(prev => !prev);
    setSelectedContractors([]);
  };

  const handleBulkContractorDelete = async () => {
    if (!window.confirm(`Mettre ${selectedContractors.length} compte(s) entrepreneur à la corbeille ? (récupérable pendant 30 jours)`)) return;
    try {
      const res = await axios.post(`${API}/contractors/bulk-delete`, { contractor_ids: selectedContractors }, { withCredentials: true });
      toast.success(`${res.data.deleted} compte(s) mis à la corbeille`);
      fetchData();
    } catch (error) { toast.error("Erreur lors de la suppression"); }
  };

  const handleRestoreContractors = async (ids) => {
    try {
      const res = await axios.post(`${API}/contractors/restore`, { contractor_ids: ids }, { withCredentials: true });
      toast.success(`${res.data.restored} compte(s) restauré(s)`);
      fetchData();
    } catch (error) { toast.error("Erreur lors de la restauration"); }
  };

  const handlePurgeContractors = async (ids) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ${ids.length} compte(s) entrepreneur ? Cette action est irréversible.`)) return;
    try {
      const res = await axios.post(`${API}/contractors/purge`, { contractor_ids: ids }, { withCredentials: true });
      toast.success(`${res.data.purged} compte(s) supprimé(s) définitivement`);
      fetchData();
    } catch (error) { toast.error("Erreur lors de la suppression définitive"); }
  };

  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments(prev => prev.includes(paymentId) ? prev.filter(id => id !== paymentId) : [...prev, paymentId]);
  };
  const togglePaymentSelectAll = () => {
    setSelectedPayments(prev => prev.length === payments.length ? [] : payments.map(tx => tx.id));
  };

  const handleBulkDeletePayments = async () => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ${selectedPayments.length} paiement(s) ? Cette action est irréversible.`)) return;
    try {
      const res = await axios.post(`${API}/admin/payments/bulk-delete`, { payment_ids: selectedPayments }, { withCredentials: true });
      toast.success(`${res.data.deleted} paiement(s) supprimé(s)`);
      fetchPayments();
      fetchAnalytics();
    } catch (error) { toast.error("Erreur lors de la suppression des paiements"); }
  };

  const handleBulkContractorStatus = async (status) => {
    const label = status === "active" ? "activer" : "suspendre";
    if (!window.confirm(`Voulez-vous ${label} ${selectedContractors.length} compte(s) entrepreneur ?`)) return;
    try {
      const res = await axios.post(`${API}/contractors/bulk-status`, { contractor_ids: selectedContractors, status }, { withCredentials: true });
      toast.success(`${res.data.updated} compte(s) mis à jour`);
      fetchData();
    } catch (error) { toast.error("Erreur lors de la mise à jour des comptes"); }
  };

  const handleForwardFiles = async (leadId) => {
    try {
      await axios.post(`${API}/admin/leads/${leadId}/forward-files`, {}, { withCredentials: true });
      toast.success("Fichiers rendus accessibles à l'entrepreneur");
      fetchData();
    } catch (error) { toast.error("Erreur"); }
  };

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const leadsTabProps = {
    API, leads, trashLeads, selectedLeads, setSelectedLeads, showTrash, filters, setFilters,
    loading, currentPage, totalPages, setCurrentPage, contractors,
    toggleTrashView, handleExport, handleBulkRestore, handleBulkPurge, toggleTrashSelectAll,
    toggleLeadSelection, handleRestoreLead, handlePurgeLead, toggleSelectAll,
    handleBulkStatus, handleBulkDelete, handleForwardFiles, handleStatusChange, handleDeleteLead, handlePublish, handleBulkPublish, fetchData
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">DM</span>
              </div>
              <span className="font-heading font-bold text-xl text-slate-900">Administration</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600 hidden sm:block">{adminEmail}</span>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-cards">
            <div className="stats-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><FileText className="w-5 h-5 text-blue-600" /></div>
                <span className="text-sm text-slate-500">Total leads</span>
              </div>
              <p className="text-3xl font-heading font-bold text-slate-900">{stats.total_leads}</p>
            </div>
            <div className="stats-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-green-600" /></div>
                <span className="text-sm text-slate-500">Nouveaux leads</span>
              </div>
              <p className="text-3xl font-heading font-bold text-slate-900">{stats.new_leads}</p>
              {stats.to_validate_leads > 0 && (
                <button type="button" onClick={() => setFilters(prev => ({ ...prev, status: "to_validate" }))} className="mt-1 text-xs font-semibold text-orange-600 hover:underline" data-testid="kpi-to-validate">
                  dont {stats.to_validate_leads} à valider →
                </button>
              )}
            </div>
            <div className="stats-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-orange-600" /></div>
                <span className="text-sm text-slate-500">Leads vendus</span>
              </div>
              <p className="text-3xl font-heading font-bold text-slate-900">{stats.assigned_leads}</p>
            </div>
            <div className="stats-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Building2 className="w-5 h-5 text-purple-600" /></div>
                <span className="text-sm text-slate-500">Entrepreneurs</span>
              </div>
              <p className="text-3xl font-heading font-bold text-slate-900">{stats.total_contractors}</p>
            </div>
            {stats.leads_by_lang && (
              <div className="stats-card sm:col-span-2 lg:col-span-4" data-testid="lang-stats-card">
                <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                  <span className="text-sm text-slate-500 font-medium">Demandes par langue :</span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">FR</span>
                    <span className="font-heading font-bold text-slate-900" data-testid="lang-stats-fr">{stats.leads_by_lang.fr}</span>
                    <span className="text-sm text-slate-400">
                      ({stats.total_leads > 0 ? Math.round((stats.leads_by_lang.fr / (stats.leads_by_lang.fr + stats.leads_by_lang.nl || 1)) * 100) : 0}%)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700">NL</span>
                    <span className="font-heading font-bold text-slate-900" data-testid="lang-stats-nl">{stats.leads_by_lang.nl}</span>
                    <span className="text-sm text-slate-400">
                      ({stats.total_leads > 0 ? Math.round((stats.leads_by_lang.nl / (stats.leads_by_lang.fr + stats.leads_by_lang.nl || 1)) * 100) : 0}%)
                    </span>
                  </div>
                  <div className="flex-1 min-w-[140px] h-2 bg-blue-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-400 float-right"
                      style={{ width: `${stats.leads_by_lang.fr + stats.leads_by_lang.nl > 0 ? (stats.leads_by_lang.nl / (stats.leads_by_lang.fr + stats.leads_by_lang.nl)) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-6 relative" data-testid="global-search">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Rechercher un lead ou un entrepreneur (nom, email, N° de lead, téléphone, ville, TVA)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9 bg-white"
              data-testid="global-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="global-search-clear">
                <XIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchResults && (
            <div className="absolute z-20 mt-2 w-full bg-white rounded-xl border border-slate-200 shadow-xl p-4 max-h-96 overflow-y-auto" data-testid="global-search-results">
              {searchResults.leads.length === 0 && searchResults.contractors.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucun résultat pour « {searchQuery} »</p>
              ) : (
                <div className="space-y-4">
                  {searchResults.leads.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Leads ({searchResults.leads.length})</p>
                      <div className="space-y-1">
                        {searchResults.leads.map((l) => (
                          <div key={l.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 rounded-lg hover:bg-slate-50 text-sm" data-testid={`search-lead-${l.id}`}>
                            <span className="font-mono text-xs text-orange-600 font-bold">{l.lead_number || "—"}</span>
                            <span className="font-medium text-slate-900">{l.name}</span>
                            <span className="text-slate-500">{l.project_type}</span>
                            <span className="text-slate-500">{l.city}</span>
                            <span className="text-slate-500">{l.email}</span>
                            <span className="text-slate-500">{l.phone}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {searchResults.contractors.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Entrepreneurs ({searchResults.contractors.length})</p>
                      <div className="space-y-1">
                        {searchResults.contractors.map((c) => (
                          <div key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 rounded-lg hover:bg-slate-50 text-sm" data-testid={`search-contractor-${c.id}`}>
                            <Building2 className="w-3.5 h-3.5 text-slate-400" />
                            <span className="font-medium text-slate-900">{c.company_name}</span>
                            <span className="text-slate-500">{c.email}</span>
                            <span className="text-slate-500">{c.phone}</span>
                            <span className="text-slate-500">{c.vat_number}</span>
                            <span className={`badge ${c.status === "active" ? "badge-active" : c.status === "suspended" ? "bg-red-100 text-red-700" : "badge-pending"}`}>
                              {c.status === "active" ? "Actif" : c.status === "suspended" ? "Suspendu" : "En attente"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList className="flex flex-wrap h-auto justify-start gap-1 w-full lg:w-auto">
            <TabsTrigger value="leads" data-testid="leads-tab">Leads</TabsTrigger>
            <TabsTrigger value="contractors" data-testid="contractors-tab">Entrepreneurs</TabsTrigger>
            <TabsTrigger value="payments" data-testid="payments-tab"><CreditCard className="w-4 h-4 mr-2" />Paiements</TabsTrigger>
            <TabsTrigger value="transfers" data-testid="transfers-tab"><Landmark className="w-4 h-4 mr-2" />Virements</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="reviews-tab"><Star className="w-4 h-4 mr-2" />Avis</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab"><Settings className="w-4 h-4 mr-2" />Paramètres</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab"><BarChart3 className="w-4 h-4 mr-2" />Analytics</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="pricing-tab"><Euro className="w-4 h-4 mr-2" />Prix des leads</TabsTrigger>
            <TabsTrigger value="blog" data-testid="blog-tab"><FileText className="w-4 h-4 mr-2" />Blog</TabsTrigger>
            <TabsTrigger value="security" data-testid="security-tab"><ShieldAlert className="w-4 h-4 mr-2" />Sécurité</TabsTrigger>
            <TabsTrigger value="journal" data-testid="journal-tab"><BellRing className="w-4 h-4 mr-2" />Journal</TabsTrigger>
          </TabsList>

          <TabsContent value="leads"><LeadsTab {...leadsTabProps} /></TabsContent>
          <TabsContent value="contractors">
            <ContractorsTab
              loading={loading} contractors={contractors} selectedContractors={selectedContractors}
              setSelectedContractors={setSelectedContractors} toggleContractorSelection={toggleContractorSelection}
              toggleContractorSelectAll={toggleContractorSelectAll} handleBulkContractorStatus={handleBulkContractorStatus}
              showContractorTrash={showContractorTrash} toggleContractorTrashView={toggleContractorTrashView}
              contractorTrash={contractorTrash} handleBulkContractorDelete={handleBulkContractorDelete}
              handleRestoreContractors={handleRestoreContractors} handlePurgeContractors={handlePurgeContractors}
              handleExportContractors={handleExportContractors} API={API}
            />
          </TabsContent>
          <TabsContent value="payments">
            <PaymentsTab
              payments={payments} pdfMonth={pdfMonth} setPdfMonth={setPdfMonth}
              handleExportPaymentsPdf={handleExportPaymentsPdf} handleSendMonthlyReport={handleSendMonthlyReport}
              selectedPayments={selectedPayments} setSelectedPayments={setSelectedPayments}
              togglePaymentSelection={togglePaymentSelection} togglePaymentSelectAll={togglePaymentSelectAll}
              handleBulkDeletePayments={handleBulkDeletePayments}
            />
          </TabsContent>
          <TabsContent value="reviews">
            <ReviewsTab reviews={reviews} reviewFilter={reviewFilter} setReviewFilter={setReviewFilter} handleModerate={handleModerate} moderatingId={moderatingId}
              selectedReviews={selectedReviews} toggleReviewSelect={toggleReviewSelect} toggleSelectAllReviews={toggleSelectAllReviews} handleBulkDeleteReviews={handleBulkDeleteReviews} />
          </TabsContent>
          <TabsContent value="settings">
            <SettingsTab adminSettings={adminSettings} setAdminSettings={setAdminSettings} handleSaveSettings={handleSaveSettings} savingSettings={savingSettings} />
          </TabsContent>
          <TabsContent value="analytics">
            <AnalyticsTab analytics={analytics} analyticsLoading={analyticsLoading}
              revenueGoal={revenueGoal} goalInput={goalInput} setGoalInput={setGoalInput} handleSaveRevenueGoal={handleSaveRevenueGoal} />
          </TabsContent>
          <TabsContent value="pricing">
            <PricingTab
              leadPricing={leadPricing} pricingLoading={pricingLoading} editingPrices={editingPrices}
              setEditingPrices={setEditingPrices} isEditingPricing={isEditingPricing} setIsEditingPricing={setIsEditingPricing}
              handleSeedPricing={handleSeedPricing} handleSaveAllPricing={handleSaveAllPricing} fetchPricing={fetchPricing}
            />
          </TabsContent>
          <TabsContent value="blog">
            <BlogTab />
          </TabsContent>
          <TabsContent value="journal"><NotificationsLogTab API={API} /></TabsContent>
          <TabsContent value="transfers"><TransfersTab API={API} /></TabsContent>
          <TabsContent value="security">
            <SecurityTab emailStats={emailStats} securityEvents={securityEvents} adminLogins={adminLogins} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
